import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/reactQuery';
import { ThemeProvider } from './context/ThemeContext';

import { AuthProvider, useAuth } from './context/AuthContext';
import { GenerationProvider } from './context/GenerationContext';
import { PrivateRoute } from './components/PrivateRoute';
import { GenerationStatus } from './components/GenerationStatus';
import { MobileBottomMenu } from './components/MobileBottomMenu';
import { useKnowledgeSignalR } from './hooks/useKnowledgeTree';
import { useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Chat from './pages/Chat';
import NotesArticle from './pages/articles/NotesArticle';
import ContentPlan from './pages/articles/ContentPlan';
import ReelsTranscription from './pages/articles/ReelsTranscription';
import Roadmap from './pages/Roadmap';
import Audience from './pages/Audience';
import Knowledge from './pages/Knowledge';
import Profile from './pages/Profile';

import Tariffs from './pages/Tariffs';
import TaskProgress from './pages/TaskProgress';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import EmailConfirmation from './pages/auth/EmailConfirmation';
import VerifyEmail from './pages/auth/VerifyEmail';
import SetupNickname from './pages/auth/SetupNickname';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LandingPage from './pages/LandingPage';
import PublicOffer from './pages/PublicOffer';
import { EditorDemoPage } from './pages/EditorDemoPage';

import CoverStyles from './pages/CoverStyles';
import KanbanDocument from './pages/KanbanDocument';
import { Mail } from './pages/Mail';
import { MailProvider } from './context/MailContext';
import ReferralRedirect from './pages/ReferralRedirect';

// Компонент для условного отображения GenerationStatus
function ConditionalGenerationStatus() {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  
  // Не показываем GenerationStatus на лендинге и странице задач
  if (location.pathname === '/' || location.pathname === '/tasks') {
    return null;
  }
  
  // Для неавторизованных и в процессе загрузки статуса авторизации — не рендерим
  if (isLoading || !isAuthenticated) {
    return null;
  }
  
  return <GenerationStatus />;
}

// Компонент для принудительного обновления URL
function URLEnforcer() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Проверяем базовые пути без параметров
    const path = location.pathname;
    const timestamp = Date.now();
    
    // Проверка для пустого/корневого пути
    if (path === "/" && location.search === "") {
      // Если мы на корневом пути, показываем лендинг
      return;
    }

    if (path === '/chat') {
      navigate(`/chat/default-${timestamp}`, { replace: true });
    } else if (path === '/studio' || path === '/dashboard') {
      navigate('/studio', { replace: true });
    } else if (path === '/roadmap') {
      navigate('/roadmap', { replace: true });
    } else if (path === '/audience') {
      navigate('/audience', { replace: true });
    } else if (path.startsWith('/articles/')) {
      // Проверяем наличие параметров в пути статей
      const parts = path.split('/');
      if (parts.length === 3) {
        // Специальная обработка для Reels
        if (parts[2] === 'reels') {
          navigate('/reels', { replace: true });
          return;
        }
        navigate(`${path}/new-${timestamp}`, { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  
  return null;
}

// Компонент для инициализации SignalR только для авторизованных пользователей
function SignalRInitializer() {
  const { isAuthenticated, isLoading } = useAuth();
  
  // ВСЕГДА вызываем хук, но передаем ему информацию об авторизации
  // Хук сам решит, нужно ли инициализировать SignalR
  useKnowledgeSignalR(isAuthenticated && !isLoading);

  return null;
}

function App() {

  // Настраиваем слушатель события навигации от лендинга
  React.useEffect(() => {
    const handleNavigateFromLanding = (event: any) => {
      const path = event.detail?.path;
      if (path) {
        // Используем window.location для прямой навигации
        window.location.href = `${window.location.origin}${path}`;
      }
    };

    // Добавляем слушатель события
    document.addEventListener('navigateFromLanding', handleNavigateFromLanding);

    // Удаляем слушатель при размонтировании
    return () => {
      document.removeEventListener('navigateFromLanding', handleNavigateFromLanding);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId="YOUR_GOOGLE_CLIENT_ID">
      <ThemeProvider>

          <AuthProvider>
            <MailProvider>
              <GenerationProvider>
                <QueryClientProvider client={queryClient}>
                <Router>
                  <div className="min-h-screen">
                    <URLEnforcer />
                    <SignalRInitializer />
                    <Routes>
                      {/* Публичные маршруты */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/auth/login" element={<Login />} />
                      <Route path="/auth/register" element={<Register />} />
                      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                      <Route path="/auth/reset-password" element={<ResetPassword />} />
                      <Route path="/auth/email-confirmation" element={<EmailConfirmation />} />
                      <Route path="/auth/verify-email" element={<VerifyEmail />} />
                      <Route path="/auth/setup-nickname" element={<SetupNickname />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/public-offer" element={<PublicOffer />} />
                      <Route path="/editor-demo" element={<EditorDemoPage />} />
                      <Route path="/r/:promoCode" element={<ReferralRedirect />} />

                      {/* Защищенные маршруты */}
                      <Route path="/studio" element={<PrivateRoute><Home /></PrivateRoute>} />
                      <Route path="/studio/:homeId" element={<PrivateRoute><Home /></PrivateRoute>} />
                      <Route path="/chat" element={<PrivateRoute><Navigate to={`/chat/default-${Date.now()}`} replace /></PrivateRoute>} />
                      <Route path="/chat/:chatId" element={<PrivateRoute><Chat /></PrivateRoute>} />
                      <Route path="/articles/notes" element={<PrivateRoute><Navigate to={`/articles/notes/new-${Date.now()}`} replace /></PrivateRoute>} />
                      <Route path="/articles/notes/:articleId" element={<PrivateRoute><NotesArticle /></PrivateRoute>} />
                      <Route path="/articles/content-plan" element={<PrivateRoute><ContentPlan /></PrivateRoute>} />
                      <Route path="/articles/content-plan/:planId" element={<PrivateRoute><ContentPlan /></PrivateRoute>} />
                      <Route path="/reels" element={<PrivateRoute><ReelsTranscription /></PrivateRoute>} />
                      <Route path="/reels/:reelsId" element={<PrivateRoute><ReelsTranscription /></PrivateRoute>} />
                      <Route path="/roadmap" element={<PrivateRoute><Roadmap /></PrivateRoute>} />
                      <Route path="/audience" element={<PrivateRoute><Audience /></PrivateRoute>} />
                      <Route path="/knowledge" element={<PrivateRoute><Navigate to="/knowledge/root" replace /></PrivateRoute>} />
                      <Route path="/knowledge/:itemId" element={<PrivateRoute><Knowledge /></PrivateRoute>} />
                      <Route path="/kanban/:itemId" element={<PrivateRoute><KanbanDocument /></PrivateRoute>} />

                      <Route path="/mail" element={<PrivateRoute><Mail /></PrivateRoute>} />
                      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                      <Route path="/tariffs" element={<PrivateRoute><Tariffs /></PrivateRoute>} />
                      <Route path="/tasks" element={<PrivateRoute><TaskProgress /></PrivateRoute>} />

                      <Route path="/cover-styles" element={<PrivateRoute><CoverStyles /></PrivateRoute>} />

                      {/* Поддержка старого маршрута для обратной совместимости */}
                      <Route path="/dashboard" element={<PrivateRoute><Navigate to="/studio" replace /></PrivateRoute>} />

                      {/* Остальные маршруты */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <ConditionalGenerationStatus />
                    <MobileBottomMenu />
                  </div>
                </Router>
                </QueryClientProvider>
              </GenerationProvider>
            </MailProvider>
          </AuthProvider>

      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;